// Plain-English descriptions of coding rules (category → GL account) for the review page.
//
// A coding rule is two halves stored in two places: the category's pattern decides which
// deductions reach the category, and the rule maps the category to a GL account. Reviewers need
// both in one sentence, plus who made the rule, so they can tell a rule a person chose from one
// CodingWiz learned.

import { pattern_to_english, regex_to_pattern } from "../categories/pattern"
import type { RuleChange, RuleMapping } from "../reason_code_mapping"

/** Who made a rule, grouped the way the page filters it. */
export type RuleMaker = "person" | "codingwiz" | "imported"

export function rule_maker(actor: string | null): RuleMaker {
  if (actor === "user") return "person"
  if (actor === "accounting_wizard") return "codingwiz"
  return "imported"
}

export function maker_label(actor: string | null, email: string | null = null): string {
  switch (rule_maker(actor)) {
    case "person":
      return email ?? "a person"
    case "codingwiz":
      return "CodingWiz"
    case "imported":
      return actor ? `import (${actor})` : "import"
  }
}

// KeHE numbers end `<DC+vendor>.<MMYY>.<seq>`; the rules spell that tail `\d+.\d+.\d+`.
const KEHE_TAIL = "\\d+.\\d+.\\d+"

/** One pattern as a phrase: `invoice number starts with "SWNEN"`. */
export function describe_pattern(field: "invoice number" | "description", regex: string): string {
  if (field === "invoice number" && regex.endsWith(KEHE_TAIL)) {
    const code = regex.slice(0, -KEHE_TAIL.length)
    if (/^[A-Z]+$/.test(code)) return `invoice code is ${code} (KeHE)`
  }
  return `${field} ${pattern_to_english(regex_to_pattern(regex))}`
}

/** Which deductions reach the rule, as a sentence, in the order the matcher tries its patterns. */
export function describe_match(rule: Pick<RuleMapping, "category_invoice_regex" | "category_remark_regex">): string {
  const parts: string[] = []
  if (rule.category_invoice_regex) parts.push(describe_pattern("invoice number", rule.category_invoice_regex))
  if (rule.category_remark_regex) parts.push(describe_pattern("description", rule.category_remark_regex))
  if (parts.length === 0) return "Applies only to deductions someone (or BigWiz) puts in this category"
  return `Applies when the ${parts.join(", or the ")}`
}

/** The GL account as shown: "418510 Store Promos…", without repeating a number the name already starts with. */
export function gl_label(rule: Pick<RuleMapping, "gl_account" | "fund_name">): string {
  return rule.fund_name.startsWith(rule.gl_account) ? rule.fund_name : `${rule.gl_account} ${rule.fund_name}`
}

/** Reasons a rule is worth a second look; empty when it looks healthy. */
export function review_flags(rule: RuleMapping): string[] {
  const flags: string[] = []
  if (!rule.enabled) flags.push("Off")
  if (!rule.category_invoice_regex && !rule.category_remark_regex) flags.push("No pattern")
  if (rule.category_deduction_count === 0) flags.push("Unused")
  if (rule_maker(rule.actor) === "codingwiz") flags.push("Made by CodingWiz")
  return flags
}

/** PURE: a rule's history as worth showing — no-op entries dropped — plus its creation entry, if recorded. */
export function rule_timeline(history: RuleChange[]): { changes: RuleChange[]; created: RuleChange | null } {
  const changes = history.filter((h: RuleChange) => h.old_gl_account === null || h.old_gl_account !== h.new_gl_account)
  const created = changes[0]?.old_gl_account === null ? changes[0] : null
  return { changes, created }
}

export type MakerFilter = "all" | RuleMaker
export type ShowFilter = "all" | "needs_review" | "off"

/** PURE: filter + sort rules for the page, most recently changed first. */
export function filter_rules(rules: RuleMapping[], search: string, maker: MakerFilter, show: ShowFilter): RuleMapping[] {
  const needle = search.trim().toLowerCase()
  return rules
    .filter((r: RuleMapping) => maker === "all" || rule_maker(r.actor) === maker)
    .filter((r: RuleMapping) => {
      if (show === "off") return !r.enabled
      if (show === "needs_review") return review_flags(r).length > 0
      return true
    })
    .filter((r: RuleMapping) => {
      if (!needle) return true
      const haystack = [
        r.category_name,
        r.category_description,
        r.category_source,
        r.category_invoice_regex,
        r.category_remark_regex,
        r.gl_account,
        r.fund_name,
        ...r.example_invoice_numbers,
      ]
      return haystack.some((v: string | null) => v?.toLowerCase().includes(needle))
    })
    .sort((a: RuleMapping, b: RuleMapping) => b.updated_at.localeCompare(a.updated_at))
}

// @vitest-environment jsdom
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest

  function rule(partial: Partial<RuleMapping>): RuleMapping {
    return {
      id: "1",
      category_id: "c",
      category_name: "Retailer Promotion",
      category_description: "Safeway Scan",
      category_source: "kehe",
      category_invoice_regex: null,
      category_remark_regex: null,
      reason_code_id: "r",
      fund_name: "Store Promos & Scan Allowances",
      gl_account: "418510",
      fund_category: null,
      external_id: null,
      enabled: true,
      start_date: null,
      end_date: null,
      conditions: {
        direct_customers: [],
        indirect_customers: [],
        direct_planned_customers: [],
        indirect_planned_customers: [],
        selling_units: [],
        rate_dollar_from: null,
        rate_dollar_to: null,
        rate_pct_from: null,
        rate_pct_to: null,
        match_field: null,
        starts_with: null,
        contains: null,
        ends_with: null,
        string_rule_excluded: false,
      },
      updated_at: "2026-09-23T00:00:00Z",
      created_at: "2026-09-23T00:00:00Z",
      actor: "user",
      actor_email: null,
      history: [],
      coded_count: 0,
      category_deduction_count: 3,
      example_invoice_numbers: [],
      ...partial,
    }
  }

  it("describes a KeHE code rule by its code", () => {
    expect(describe_match(rule({ category_invoice_regex: "SWNEN\\d+.\\d+.\\d+" }))).toBe("Applies when the invoice code is SWNEN (KeHE)")
  })

  it("describes other patterns through the category pattern builder", () => {
    expect(describe_match(rule({ category_invoice_regex: "LCPV\\d+" }))).toBe("Applies when the invoice number is exactly “LCPV” then numbers")
    expect(describe_match(rule({ category_remark_regex: "promobilling" }))).toContain("description")
  })

  it("says plainly when a rule has no pattern", () => {
    expect(describe_match(rule({}))).toBe("Applies only to deductions someone (or BigWiz) puts in this category")
    expect(review_flags(rule({}))).toContain("No pattern")
  })

  it("does not repeat a GL number the account name already starts with", () => {
    expect(gl_label(rule({ gl_account: "418510", fund_name: "Store Promos" }))).toBe("418510 Store Promos")
    expect(gl_label(rule({ gl_account: "4602", fund_name: "4602 Gross Revenue" }))).toBe("4602 Gross Revenue")
  })

  it("rule_timeline drops no-op changes and only treats a creation entry as the maker", () => {
    const change = (old_gl: string | null, new_gl: string) => ({ changed_at: null, actor: "user", actor_email: null, old_gl_account: old_gl, new_gl_account: new_gl })
    expect(rule_timeline([change("4504", "4504"), change("4504", "4604")])).toEqual({ changes: [change("4504", "4604")], created: null })
    expect(rule_timeline([change(null, "418510")]).created).toEqual(change(null, "418510"))
  })

  it("labels who made a rule", () => {
    expect(maker_label("accounting_wizard")).toBe("CodingWiz")
    expect(maker_label("user", "taylor@nancys.com")).toBe("taylor@nancys.com")
    expect(maker_label(null)).toBe("import")
    expect(rule_maker("backfill: best guess")).toBe("imported")
  })

  it("filters by maker, review state and search, newest change first", () => {
    const rules = [
      rule({ id: "a", actor: "accounting_wizard", updated_at: "2026-09-01T00:00:00Z", category_invoice_regex: "SWNEN\\d+.\\d+.\\d+" }),
      rule({ id: "b", actor: "user", updated_at: "2026-09-20T00:00:00Z", category_invoice_regex: "GEN\\d+.\\d+.\\d+" }),
      rule({ id: "c", actor: "user", enabled: false, updated_at: "2026-09-10T00:00:00Z", category_invoice_regex: "KP\\d+" }),
    ]
    expect(filter_rules(rules, "", "all", "all").map((r: RuleMapping) => r.id)).toEqual(["b", "c", "a"])
    expect(filter_rules(rules, "", "codingwiz", "all").map((r: RuleMapping) => r.id)).toEqual(["a"])
    expect(filter_rules(rules, "", "all", "off").map((r: RuleMapping) => r.id)).toEqual(["c"])
    expect(filter_rules(rules, "", "all", "needs_review").map((r: RuleMapping) => r.id)).toEqual(["c", "a"])
    expect(filter_rules(rules, "swnen", "all", "all").map((r: RuleMapping) => r.id)).toEqual(["a"])
    expect(filter_rules(rules, "418510", "all", "all")).toHaveLength(3)
  })
}
